import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import { dataAPI } from '../../helpers/api_service';

const NotificationsPanel = ({ notifications: notificationsData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [expandedNotifications, setExpandedNotifications] = useState({});
  const [overflowingMessages, setOverflowingMessages] = useState({});
  const messageRefs = useRef({});

  // Process notifications data from API
  const notifications = useMemo(() => notificationsData || [], [notificationsData]);

  const toggleExpandedMessage = (notificationId) => {
    setExpandedNotifications((prev) => ({
      ...prev,
      [notificationId]: !prev[notificationId]
    }));
  };

  useEffect(() => {
    const nextOverflowState = {};

    notifications.forEach((notification) => {
      const element = messageRefs.current[notification.id];
      if (!element) return;

      nextOverflowState[notification.id] = element.scrollHeight > element.clientHeight + 1;
    });

    setOverflowingMessages(nextOverflowState);
  }, [notifications, expandedNotifications]);

  // Function to format time ago
  const getTimeAgo = (sentTime) => {
    try {
      const date = new Date(sentTime);
      const distance = formatDistanceToNow(date, { addSuffix: true });
      // Remove "about" keyword from timing
      return distance.replace(/^about\s/, '');
    } catch (error) {
      return 'Just now';
    }
  };

  // Function to generate avatar initials from sender name
  const getAvatarInitials = (sender) => {
    if (!sender?.full_name) return 'NA';
    return sender.full_name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle notification click with smart navigation (same as topbar notifications)
  const handleNotificationClick = async (notification) => {
    console.log('🎯 Notification clicked:', notification);
    
    // Mark notification as read if not already read
    if (notification.id && !notification.is_read) {
      try {
        // Disable global loading indicator for this API call to avoid popup overlay
        await dataAPI.markNotificationRead(notification.id, { showLoading: false });
        console.log(`✅ Marked notification ${notification.id} as read`);
      } catch (error) {
        console.error('❌ Error marking notification as read:', error);
        // Continue with navigation even if marking as read fails
      }
    }
    
    // Check if notification has related entity information
    if (notification.related_entity_id && notification.related_entity_type) {
      
      switch (notification.related_entity_type.toLowerCase()) {
        case 'task':
          // Navigate to task details page
          console.log(`📋 Navigating to task: ${notification.related_entity_id}`);
          navigate(`/project/1/task/${notification.related_entity_id}`, {
            state: {
              fromNotification: true,
              notificationId: notification.id
            }
          });
          break;
          
        case 'project':
        case 'projects':
          // Navigate to projects page
          console.log(`📁 Navigating to projects page`);
          navigate('/projects', {
            state: {
              fromNotification: true,
              notificationId: notification.id,
              focusProjectId: notification.related_entity_id
            }
          });
          break;
          
        case 'document':
          // Navigate to documents page
          console.log(`📄 Navigating to documents page`);
          navigate('/documents', {
            state: {
              fromNotification: true,
              notificationId: notification.id,
              focusDocumentId: notification.related_entity_id
            }
          });
          break;
          
        default:
          // For other types, don't navigate
          console.log(`ℹ️ No specific route for type: ${notification.related_entity_type}`);
          break;
      }
    }
  };

  return (
    <div style={{
      backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
      height: '280px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        margin: '0 0 20px 0',
        flexShrink: 0,
        color: mode === 'dark' ? '#ffffff' : '#333333',
        fontFamily: 'Urbanist, Arial, sans-serif'
      }}>
        Notifications
      </h3>

      {/* Notifications List */}
      <div 
        className="scrollable-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // Internet Explorer/Edge
        }}
      >
        {notifications.length > 0 ? notifications.map((notification, index) => {
          const timeAgo = getTimeAgo(notification.sent_time);
          const avatarInitials = getAvatarInitials(notification.sender);
          const isExpanded = !!expandedNotifications[notification.id];
          const showMoreButton = !!overflowingMessages[notification.id] || isExpanded;
          
          // Check if notification has known clickable type (same logic as topbar)
          const isClickable = notification.related_entity_id && notification.related_entity_type && 
            ['task', 'project', 'projects', 'document'].includes(notification.related_entity_type.toLowerCase());
          
          return (
            <div
              key={notification.id}
              onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
              style={{
                display: 'flex',
                gap: '12px',
                padding: '16px 0',
                borderBottom: index < notifications.length - 1 ? (mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0') : 'none',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'background-color 0.2s ease',
                borderRadius: '8px',
                marginLeft: '-8px',
                marginRight: '-8px',
                paddingLeft: '8px',
                paddingRight: '8px'
              }}
              onMouseEnter={isClickable ? (e) => {
                e.currentTarget.style.backgroundColor = mode === 'dark' ? '#252525' : '#f5f5f5';
              } : undefined}
              onMouseLeave={isClickable ? (e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              } : undefined}
            >
              {/* Avatar */}
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: '#26c3c0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
                fontFamily: 'Urbanist, Arial, sans-serif',
                flexShrink: 0
              }}>
                {avatarInitials}
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '6px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    margin: 0,
                    color: mode === 'dark' ? '#ffffff' : '#333333',
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}>
                    {notification.title}
                  </h4>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#b0b0b0',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                    whiteSpace: 'nowrap'
                  }}>
                    {timeAgo}
                  </span>
                </div>
                <p style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: mode === 'dark' ? '#b0b0b0' : '#333333',
                  margin: 0,
                  lineHeight: '1.4',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  overflow: isExpanded ? 'visible' : 'hidden',
                  display: isExpanded ? 'block' : '-webkit-box',
                  WebkitLineClamp: isExpanded ? 'unset' : 4,
                  WebkitBoxOrient: 'vertical'
                }}
                ref={(element) => {
                  messageRefs.current[notification.id] = element;
                }}>
                  {notification.message}
                </p>
                {showMoreButton && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpandedMessage(notification.id);
                    }}
                    style={{
                      marginTop: '6px',
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      color: '#26c3c0',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                      fontFamily: 'Urbanist, Arial, sans-serif'
                    }}
                  >
                    {isExpanded ? 'less' : 'more'}
                  </button>
                )}
              </div>
            </div>
          );
        }) : (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            paddingTop: '10px'
          }}>
            {/* Bell circle */}
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              backgroundColor: mode === 'dark' ? '#262626' : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              boxShadow: mode === 'dark'
                ? '0 10px 25px rgba(0,0,0,0.35)'
                : '0 10px 25px rgba(15,23,42,0.12)'
            }}>
              <span style={{
                fontSize: '32px',
                filter: mode === 'dark' ? 'grayscale(0.1) brightness(1.1)' : 'grayscale(0.3) brightness(0.8)'
              }}>
                🔔
              </span>
            </div>

            {/* Title */}
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              color: mode === 'dark' ? '#ffffff' : '#111827',
              fontFamily: 'Urbanist, Arial, sans-serif',
              marginBottom: '4px'
            }}>
              You're all caught up!
            </div>

            {/* Subtitle */}
            <div style={{
              fontSize: '13px',
              color: mode === 'dark' ? '#9ca3af' : '#6b7280',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              No new notifications
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
